import { Injectable, inject } from '@angular/core';
import { ProductClassifications } from '@app/core/enums/product-classifications';
import { NotificationService } from '@app/core/services/notification.service';
import { TradeInImeiErrorType } from '@app/sales/modules/mobilephone/models/trade-in-enums';
import { EquipmentService } from '@app/sales/services/equipment.service';
import { IconNames } from '@app/shared/enums/icon-names';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { AcceptTradeInQuoteResponse, TradeInProduct, VendorSubsidyResponse } from '@sales/models/trade-in-product.model';
import { SetSelectedMobilePhoneIdAction } from '../customer-product-mobilephone/customer-product-mobilephone.actions';
import { CustomerProductMobilePhoneSelectors } from '../customer-product-mobilephone/customer-product-mobilephone.selectors';
import { AddShippingItemAction, RemoveShippingItemAction } from '../logistics/logistics.actions';
import {
  AcceptTradeInQuoteAction,
  AcceptTradeInVoucherAction,
  AddInteractedTradeInStepAction,
  CreateOrUpdateTradeInVendorSubsidyVoucher,
  CreateTradeInVoucherAction,
  CreateTradeInVoucherFailureAction,
  CreateTradeInVoucherSuccessAction,
  GetGradingQuestionsAction,
  NotifyUserOfVendorSubsidyAction,
  QuoteResetAction,
  SelectTradeInProductAction,
  SetIsCalculatingTradeInValueAction,
  SetIsCalculatingVendorSubsidyAction,
  SetIsValidatingImeiAction,
  SetSelectedStorageCapacityAction,
  SetSelectedTradeInStepIndexAction,
  TradeInProductResetAction,
  ValidateImeiFailureAction,
  ValidateImeiSuccessAction,
} from './customer-product-trade-in.actions';
import { VoucherService } from '@app/sales/services/voucher.service';

export interface TradeInStateModel {
  tradeIn: TradeInProduct;
  vendorSubsidy: VendorSubsidyResponse;
  quote: AcceptTradeInQuoteResponse;
  isValidatingImei: boolean;
  isCalculatingTradeInValue: boolean;
  isCalculatingVendorSubsidy: boolean;
  hasAcceptedTradeInVoucher: boolean;
  selectedStep: number;
  interactedSteps: number[];
  isTradeInProductSelected: boolean;
}

const initialTradeInState: TradeInStateModel = {
  tradeIn: {
    specifications: {
      deviceModel: '',
      deviceVendor: '',
      deviceDisplayName: '',
      imei: '',
      validateTradeInImeiError: TradeInImeiErrorType.NoError,
      storageCapacityAlternatives: [],
      selectedStorageCapacity: {
        size: 0,
        unit: '',
      },
    },
    gradingQuestions: [],
    tradeInVoucher: {
      tradeInId: 0,
      approved: false,
      estimatedPrice: 0,
    },
  },
  vendorSubsidy: null,
  quote: null,
  isValidatingImei: false,
  isCalculatingTradeInValue: false,
  isCalculatingVendorSubsidy: false,
  hasAcceptedTradeInVoucher: false,
  selectedStep: 0,
  interactedSteps: [],
  isTradeInProductSelected: false,
};

@State<TradeInStateModel>({
  name: 'customerProductsTradeIn',
  defaults: initialTradeInState,
})
@Injectable()
export class CustomerProductTradeInState {
  private notificationService = inject(NotificationService);
  private equipmentService = inject(EquipmentService);
  private voucherService = inject(VoucherService);
  private store = inject(Store);

  @Action(SetSelectedTradeInStepIndexAction)
  public setSelectedStepIndex(
    { getState, patchState, dispatch }: StateContext<TradeInStateModel>,
    { index }: SetSelectedTradeInStepIndexAction
  ) {
    const state = getState();
    if (state.selectedStep !== index) {
      patchState({ selectedStep: index });
    }

    dispatch(new AddInteractedTradeInStepAction(index));
  }

  @Action(AddInteractedTradeInStepAction)
  public addInteractedStep(
    { getState, patchState }: StateContext<TradeInStateModel>,
    { index }: AddInteractedTradeInStepAction
  ) {
    const interactedSteps = getState().interactedSteps;
    if (!interactedSteps.includes(index)) {
      patchState({ interactedSteps: [...interactedSteps, index] });
    }
  }

  @Action(SetIsValidatingImeiAction)
  public setIsValidatingImei(
    { patchState }: StateContext<TradeInStateModel>,
    { isValidatingImei }: SetIsValidatingImeiAction
  ) {
    patchState({ isValidatingImei });
  }

  @Action(SetIsCalculatingTradeInValueAction)
  public setIsCalculatingTradeInValue(
    { patchState }: StateContext<TradeInStateModel>,
    { isCalculatingTradeInValue }: SetIsCalculatingTradeInValueAction
  ) {
    patchState({ isCalculatingTradeInValue });
  }

  @Action(SetIsCalculatingVendorSubsidyAction)
  public setIsCalculatingVendorSubsidy(
    { patchState }: StateContext<TradeInStateModel>,
    { isCalculatingVendorSubsidy }: SetIsCalculatingVendorSubsidyAction
  ) {
    patchState({ isCalculatingVendorSubsidy });
  }

  @Action(ValidateImeiSuccessAction)
  public validateImeiSuccess(
    { getState, patchState }: StateContext<TradeInStateModel>,
    { imeiValidation, imei }: ValidateImeiSuccessAction
  ) {
    const { deviceModel, deviceVendor, deviceDisplayName, validateTradeInImeiError, storageCapacityAlternatives } =
      imeiValidation;
    const selectedStorageCapacity = getState().tradeIn?.specifications.selectedStorageCapacity;
    const tradeInVoucher = getState().tradeIn.tradeInVoucher;
    const isSelectedStorageCapacityAvailable = storageCapacityAlternatives.some(
      storageCapacity =>
        storageCapacity.size === selectedStorageCapacity?.size && storageCapacity.unit === selectedStorageCapacity?.unit
    );

    patchState({ isValidatingImei: false });
    patchState({
      tradeIn: {
        specifications: {
          deviceModel: deviceModel,
          deviceVendor: deviceVendor,
          deviceDisplayName: deviceDisplayName,
          imei: imei,
          validateTradeInImeiError: validateTradeInImeiError || null,
          storageCapacityAlternatives: storageCapacityAlternatives || null,
          selectedStorageCapacity: isSelectedStorageCapacityAvailable ? selectedStorageCapacity : null,
        },
        gradingQuestions: getState().tradeIn?.gradingQuestions || null,
        tradeInVoucher: tradeInVoucher,
      },
    });
  }

  @Action(ValidateImeiFailureAction)
  public validateImeiFailure(
    { patchState, getState }: StateContext<TradeInStateModel>,
    { error }: ValidateImeiFailureAction
  ) {
    this.notificationService.error(error);

    const { tradeInVoucher, gradingQuestions } = getState().tradeIn;

    patchState({ isValidatingImei: false });
    patchState({
      tradeIn: {
        specifications: {
          deviceModel: null,
          deviceVendor: null,
          deviceDisplayName: null,
          imei: null,
          validateTradeInImeiError: error,
          storageCapacityAlternatives: null,
          selectedStorageCapacity: {
            size: null,
            unit: null,
          },
        },
        gradingQuestions: gradingQuestions || null,
        tradeInVoucher: tradeInVoucher || null,
      },
    });

    throw error;
  }

  @Action(SetSelectedStorageCapacityAction)
  public setSelectedStorageCapacity(
    { getState, patchState }: StateContext<TradeInStateModel>,
    { storageCapacity }: SetSelectedStorageCapacityAction
  ) {
    const tradeIn = getState().tradeIn;
    if (storageCapacity === tradeIn?.specifications?.selectedStorageCapacity) return;

    patchState({
      tradeIn: {
        ...tradeIn,
        specifications: {
          ...tradeIn?.specifications,
          selectedStorageCapacity: storageCapacity,
        },
      },
    });
  }

  @Action(GetGradingQuestionsAction)
  public getGradingQuestions({ patchState, getState }: StateContext<TradeInStateModel>) {
    const tradeIn = getState().tradeIn;

    this.equipmentService.getGradingQuestionsTradeIn().subscribe(res => {
      (patchState({
        tradeIn: {
          ...tradeIn,
          gradingQuestions: res,
        },
      }),
        error => {
          this.notificationService.error('Feil ved henting av tilstandspørsmål');
          throw error;
        });
    });
  }

  @Action(TradeInProductResetAction)
  public tradeInProductReset(ctx: StateContext<TradeInStateModel>) {
    if (ctx.getState().hasAcceptedTradeInVoucher) {
      const id = ctx.getState().quote?.voucherId;
      ctx.dispatch(new RemoveShippingItemAction(id, ProductClassifications.ReturnBox));
    }

    ctx.patchState(initialTradeInState);
  }

  @Action(QuoteResetAction)
  public quoteReset(ctx: StateContext<TradeInStateModel>) {
    if (ctx.getState().hasAcceptedTradeInVoucher) {
      ctx.dispatch(new TradeInProductResetAction());
      return;
    }

    ctx.patchState({
      quote: null,
      vendorSubsidy: null,
    });
  }

  @Action(CreateTradeInVoucherAction)
  public createTradeInVoucher(
    { dispatch }: StateContext<TradeInStateModel>,
    { tradeInRequest }: CreateTradeInVoucherAction
  ) {
    dispatch(new SetIsCalculatingTradeInValueAction(true));

    this.equipmentService.createTradeInVoucher(tradeInRequest).subscribe(
      res => {
        dispatch([new CreateTradeInVoucherSuccessAction(res), new SetIsCalculatingTradeInValueAction(false)]);
      },
      error => {
        dispatch([new CreateTradeInVoucherFailureAction(), new SetIsCalculatingTradeInValueAction(false)]);
        throw error;
      }
    );
  }

  @Action(CreateTradeInVoucherSuccessAction)
  public createTradeInVoucherSuccess(
    { getState, patchState, dispatch }: StateContext<TradeInStateModel>,
    { tradeInVoucher }: CreateTradeInVoucherSuccessAction
  ) {
    const tradeIn = getState().tradeIn;

    patchState({
      tradeIn: {
        ...tradeIn,
        tradeInVoucher,
      },
    });

    dispatch(new AcceptTradeInQuoteAction(tradeInVoucher.tradeInId));
  }

  @Action(CreateTradeInVoucherFailureAction)
  public createTradeInVoucherFailure({ getState, patchState }: StateContext<TradeInStateModel>) {
    const tradeIn = getState().tradeIn;

    this.notificationService.error('Feil ved beregning av takst');

    patchState({
      tradeIn: {
        ...tradeIn,
        tradeInVoucher: null,
      },
    });
  }

  @Action(AcceptTradeInQuoteAction)
  public acceptTradeInQuote(
    { getState, patchState, dispatch }: StateContext<TradeInStateModel>,
    { tradeInId }: AcceptTradeInQuoteAction
  ) {
    this.equipmentService.acceptQuote(tradeInId).subscribe(
      res => {
        if (getState().hasAcceptedTradeInVoucher) {
          const id = getState().quote?.voucherId;
          dispatch(new RemoveShippingItemAction(id, ProductClassifications.ReturnBox));
        }

        patchState({
          quote: res,
        });

        const gtins = this.store.selectSnapshot(CustomerProductMobilePhoneSelectors.getMobilePhoneGtins);
        if (gtins.length > 0) dispatch(new CreateOrUpdateTradeInVendorSubsidyVoucher());
      },
      error => {
        this.notificationService.error('Feil ved akseptering av takst');
        throw error;
      }
    );
  }

  @Action(CreateOrUpdateTradeInVendorSubsidyVoucher)
  public createOrUpdateTradeInVendorSubsidyVoucher(
    { getState, patchState, dispatch }: StateContext<TradeInStateModel>,
    { isTriggeredFromMobile }: CreateOrUpdateTradeInVendorSubsidyVoucher
  ) {
    const tradeInVoucher = getState().quote?.voucherId;
    if (!tradeInVoucher) return;

    dispatch(new SetIsCalculatingVendorSubsidyAction(true));

    const gtins = this.store.selectSnapshot(CustomerProductMobilePhoneSelectors.getMobilePhoneGtins);
    const vendorSubsidyRequest = { purchasedDeviceGtins: gtins, tradeInVoucher };

    this.voucherService.createOrUpdateVendorSubsidyVoucher(vendorSubsidyRequest).subscribe(
      vendorSubsidy => {
        if (isTriggeredFromMobile) dispatch(new NotifyUserOfVendorSubsidyAction(getState().vendorSubsidy, vendorSubsidy));

        patchState({
          vendorSubsidy: vendorSubsidy?.voucherAmount?.amountIncludingVAT ? vendorSubsidy : null,
        });
        dispatch(new SetIsCalculatingVendorSubsidyAction(false));
      },
      error => {
        this.notificationService.error('Feil ved oppretting eller oppdatering av produsentsubsidie');
        dispatch(new SetIsCalculatingVendorSubsidyAction(false));
        throw error;
      }
    );
  }

  @Action(NotifyUserOfVendorSubsidyAction)
  public notifyUserOfVendorSubsidy({}, { existingSubsidy, newSubsidy }: NotifyUserOfVendorSubsidyAction) {
    const existingVendorSubsidyPrice = existingSubsidy?.voucherAmount.amountIncludingVAT;
    const existingVendorSubsidyTitle = existingSubsidy?.displayInfo?.title || 'Kampanje';

    const newVendorSubsidyPrice = newSubsidy?.voucherAmount.amountIncludingVAT;
    const newVendorSubsidyTitle = newSubsidy?.displayInfo?.title || 'Kampanje';

    if (existingVendorSubsidyPrice && newVendorSubsidyPrice && existingVendorSubsidyPrice !== newVendorSubsidyPrice) {
      let message = `${existingVendorSubsidyTitle} byttet ut med ${newVendorSubsidyTitle}`;
      if (newVendorSubsidyPrice > existingVendorSubsidyPrice) message += ' (mer rabatt)';
      this.notificationService.success(message);
    } else if (!existingVendorSubsidyPrice && newVendorSubsidyPrice) {
      this.notificationService.success(`${newVendorSubsidyTitle} lagt til`);
    } else if (existingVendorSubsidyPrice && !newVendorSubsidyPrice) {
      this.notificationService.info(`${existingVendorSubsidyTitle} fjernet`);
    }
  }

  @Action(AcceptTradeInVoucherAction)
  public acceptTradeInVoucher({ getState, patchState, dispatch }: StateContext<TradeInStateModel>) {
    patchState({
      hasAcceptedTradeInVoucher: true,
    });

    const voucherId = getState().quote?.voucherId;
    dispatch(
      new AddShippingItemAction({
        id: voucherId,
        name: 'Returboks',
        offeringId: null,
        productClassification: ProductClassifications.ReturnBox,
        image: '',
        icon: IconNames.ice_delivery,
      })
    );
  }

  @Action(SelectTradeInProductAction)
  public selectTradeInProduct(
    { dispatch, patchState }: StateContext<TradeInStateModel>,
    { isSelected }: SelectTradeInProductAction
  ) {
    if (isSelected) dispatch(new SetSelectedMobilePhoneIdAction(null));

    patchState({
      isTradeInProductSelected: isSelected,
    });
  }
}
